import { FC, useEffect, useMemo, useState } from 'react';

import type { SanityCountdownBlock } from '@lib/sanity/types';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DIGIT_PAD_LENGTH = 2;
const MS_PER_MINUTE = MS_PER_SECOND * SECONDS_PER_MINUTE;
/** Shown until the first client-side tick so the band never pops in. */
const DIGIT_PLACEHOLDER = '--';

type Remaining = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getRemaining = (deadlineMs: number): Remaining => {
  const totalSeconds = Math.max(
    0,
    Math.floor((deadlineMs - Date.now()) / MS_PER_SECOND)
  );
  const days = Math.floor(
    totalSeconds / (SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY)
  );
  const hours = Math.floor(
    (totalSeconds / (SECONDS_PER_MINUTE * MINUTES_PER_HOUR)) % HOURS_PER_DAY
  );
  const minutes = Math.floor(
    (totalSeconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR
  );
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return { days, hours, minutes, seconds };
};

const hasRunOut = (remaining: Remaining): boolean =>
  remaining.days === 0 &&
  remaining.hours === 0 &&
  remaining.minutes === 0 &&
  remaining.seconds === 0;

const formatDigits = (value: number): string =>
  String(value).padStart(DIGIT_PAD_LENGTH, '0');

type CountdownProps = {
  countdown: SanityCountdownBlock;
};

const Countdown: FC<CountdownProps> = ({ countdown }) => {
  const { isEnabled, headline, deadline, expiredMessage } = countdown;
  const deadlineMs = useMemo(() => new Date(deadline).getTime(), [deadline]);

  // Computed on the client only, so the statically rendered HTML never bakes in
  // a stale clock (which would cause a hydration mismatch).
  const [remaining, setRemaining] = useState<Remaining | null>(null);
  const [shouldShowSeconds, setShouldShowSeconds] = useState(true);

  useEffect(() => {
    if (isEnabled === false) return undefined;

    // A once-a-second digit flip is motion: under prefers-reduced-motion drop
    // the seconds box and update once a minute instead.
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    setShouldShowSeconds(!prefersReducedMotion);

    const tick = () => {
      const next = getRemaining(deadlineMs);
      setRemaining(next);
      // The expired view is static; stop re-rendering once the offer closes.
      if (hasRunOut(next)) clearInterval(intervalId);
    };
    const intervalId = setInterval(
      tick,
      prefersReducedMotion ? MS_PER_MINUTE : MS_PER_SECOND
    );
    tick();
    return () => clearInterval(intervalId);
  }, [deadlineMs, isEnabled]);

  if (isEnabled === false) return null;

  const hasExpired = remaining !== null && hasRunOut(remaining);

  const units = [
    { label: 'Days', value: remaining && formatDigits(remaining.days) },
    { label: 'Hrs', value: remaining && formatDigits(remaining.hours) },
    { label: 'Min', value: remaining && formatDigits(remaining.minutes) },
    ...(shouldShowSeconds
      ? [{ label: 'Sec', value: remaining && formatDigits(remaining.seconds) }]
      : [])
  ];

  return (
    <section className="bg-landing-ink px-6 py-12 text-center">
      <div className="mx-auto w-full max-w-md md:max-w-2xl">
        {headline && (
          <p className="cms-eyebrow mb-4 text-[color:var(--accent)]">
            {headline}
          </p>
        )}

        {hasExpired ? (
          <p className="text-lg font-bold text-white">
            {expiredMessage ?? 'This offer has now closed.'}
          </p>
        ) : (
          <div
            role="timer"
            aria-label={headline ?? 'Offer countdown'}
            className="flex justify-center gap-2.5"
          >
            {units.map(({ label, value }) => (
              <div
                key={label}
                className="w-[64px] rounded-xl border border-white/10 bg-white/[0.06] py-2.5"
              >
                <span className="block text-2xl font-bold tabular-nums text-white">
                  {value ?? DIGIT_PLACEHOLDER}
                </span>
                <span className="mt-1 block text-[0.625rem] font-bold uppercase tracking-[0.08em] text-white/70">
                  {label}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Countdown;
